import { useEffect, useState } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { Chip } from '@/components/Chip/Chip';
import { DeleteButton } from '@/components/DeleteButton/DeleteButton';
import { PageEditor } from '@/components/PageEditor/PageEditor';
import { PdfViewer } from '@/components/PdfViewer/PdfViewer';
import { Spinner } from '@/components/Spinner/Spinner';
import type { BinderyJobSummary, FileDocument } from '@/graphql/files';
import { isJobInFlight, type FileEditInput } from '@/lib/page-edits';
import { formatBytes, formatDate } from '@/lib/format';

import styles from './DocumentModal.module.css';

export interface DocumentModalProps {
  file: FileDocument | null;
  loading?: boolean;
  error?: string | null;
  /** How many page images the query asked for, so a truncated rail says so. */
  railLimit: number;
  onClose: () => void;
  /** Omitted, the modal offers no delete at all. */
  onDelete?: (fileId: string) => void;
  deleting?: boolean;
  deleted?: boolean;
  deleteError?: string | null;
  /** Omitted, the modal offers no editing. Resolves true once Keystone took the edit. */
  onSaveEdit?: (
    fileId: string,
    edits: FileEditInput,
    expectedVersion: number | null,
  ) => Promise<boolean>;
  editSaving?: boolean;
  editError?: string | null;
  /** Called as the editor opens, so a refusal from an earlier draft is not shown against a new one. */
  onStartEdit?: () => void;
  /** The last edit sent from this modal, and where its job has got to. */
  editJob?: EditJob | null;
  /** Omitted, the modal offers no revert. */
  onRevert?: (fileId: string) => void;
  reverting?: boolean;
  revertError?: string | null;
}

export interface EditJob {
  versionNo: number;
  job: BinderyJobSummary;
}

/**
 * The document view: a page rail on the left, the document itself in the
 * middle, and everything Keystone knows about the row on the right.
 *
 * The rail is Keystone's own `PAGE` renditions rather than a client-side
 * rasterisation. app-web draws its rail with pdf.js in every reader's browser;
 * here the whole question is whether the pipeline *produced* those images, so
 * rendering them ourselves would answer a question nobody asked and hide the
 * one that was.
 *
 * It is a **display, not a control**. A rail that swapped the viewer for a
 * single page image was a one-way trip — nothing put the document back — and
 * page selection is not what this rail is for anyway: it is here to show
 * whether Bindery drew the images at all, which is answered by looking at it.
 */
export function DocumentModal({
  file,
  loading = false,
  error = null,
  railLimit,
  onClose,
  onDelete,
  deleting = false,
  deleted = false,
  deleteError = null,
  onSaveEdit,
  editSaving = false,
  editError = null,
  onStartEdit,
  editJob = null,
  onRevert,
  reverting = false,
  revertError = null,
}: DocumentModalProps) {
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // Escape leaves the editor rather than discarding the document view with it.
      if (event.key === 'Escape') {
        if (editing) {
          setEditing(false);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, editing]);

  const pages = file?.pages ?? [];
  const title = file?.filename ?? (loading ? 'Loading…' : 'Document');
  const headVersion = file ? headVersionNo(file) : null;
  const editPageCount = file ? editablePageCount(file, railLimit) : null;
  const inFlight = isJobInFlight(editJob?.job);
  const locked = inFlight && !editing && Boolean(file) && !error;
  const canEdit = Boolean(onSaveEdit && file && !deleted && editPageCount !== null) && !inFlight;
  const canRevert =
    Boolean(onRevert && file && !deleted && headVersion !== null && headVersion > 1) && !inFlight;

  const onSave = async (edits: FileEditInput) => {
    if (file && onSaveEdit && (await onSaveEdit(file.id, edits, headVersion))) {
      setEditing(false);
    }
  };

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <section
        aria-label={`Document: ${title}`}
        aria-modal="true"
        className={styles.dialog}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
      >
        <header className={styles.head}>
          <h2 className={styles.title}>{title}</h2>
          <div className={styles.headActions}>
            {file?.originalDownloadUrl ? (
              <a className={styles.link} href={file.originalDownloadUrl}>
                Original
              </a>
            ) : null}
            {file?.canonicalDownloadUrl ? (
              <a
                className={styles.link}
                href={file.canonicalDownloadUrl}
                rel="noreferrer"
                target="_blank"
              >
                Canonical PDF
              </a>
            ) : null}
            {canRevert && file && !editing ? (
              <Button loading={reverting} onClick={() => onRevert?.(file.id)} variant="ghost">
                Revert to original
              </Button>
            ) : null}
            {canEdit && !editing ? (
              <Button
                onClick={() => {
                  onStartEdit?.();
                  setEditing(true);
                }}
                variant="ghost"
              >
                Edit pages
              </Button>
            ) : null}
            {onDelete && file && !file.extAppApiId && !deleted ? (
              <DeleteButton
                busy={deleting}
                label={file.filename ?? 'this file'}
                onDelete={() => onDelete(file.id)}
              />
            ) : null}
            <button className={styles.close} onClick={onClose} type="button">
              Close
            </button>
          </div>
        </header>

        {deleted ? (
          <div className={styles.message}>
            <Callout tone="danger" title="Deleted">
              This file is soft-deleted in Keystone: every version, canonical and rendition
              alongside it, and no further signed URL can be minted. It stays listed until the page
              is refreshed.
            </Callout>
          </div>
        ) : null}

        {deleteError ? (
          <div className={styles.message}>
            <Callout tone="danger" title="Delete failed">
              {deleteError}
            </Callout>
          </div>
        ) : null}

        {revertError ? (
          <div className={styles.message}>
            <Callout tone="danger" title="Revert failed">
              {revertError}
            </Callout>
          </div>
        ) : null}

        {editJob && !editing && !locked ? <EditStatus editJob={editJob} /> : null}

        {editing && file && editPageCount !== null ? (
          <PageEditor
            error={editError}
            onCancel={() => setEditing(false)}
            onSave={onSave}
            pageCount={editPageCount}
            pages={pages}
            saving={editSaving}
          />
        ) : error ? (
          <div className={styles.message}>
            <Callout tone="danger" title="Keystone request failed">
              {error}
            </Callout>
          </div>
        ) : !file ? (
          <p className={styles.message} role="status">
            Asking Keystone…
          </p>
        ) : (
          <div className={styles.body}>
            <nav aria-label="Pages" className={styles.rail}>
              {pages.length ? (
                <>
                  <ol className={styles.railList}>
                    {pages.map((page) => (
                      <li className={styles.railItem} key={page.id}>
                        <img
                          alt={`Page ${page.pageNo}`}
                          className={styles.railImage}
                          loading="lazy"
                          src={page.downloadUrl}
                        />
                        <span className={styles.railNumber}>{page.pageNo}</span>
                      </li>
                    ))}
                  </ol>
                  {pages.length === railLimit && (file.pageCount ?? 0) > railLimit ? (
                    <p className={styles.railNote}>
                      First {railLimit} of {file.pageCount} pages — the rail is windowed because
                      Keystone signs one URL per image.
                    </p>
                  ) : null}
                </>
              ) : (
                <p className={styles.railNote}>
                  No page renditions for these bytes. `productionizeFile(renditions: [PAGE])` is the
                  lever.
                </p>
              )}
            </nav>

            <div className={styles.viewer}>
              {file.canonicalDownloadUrl ? (
                <PdfViewer title={title} url={file.canonicalDownloadUrl} />
              ) : (
                <p className={styles.railNote}>
                  No canonical PDF to render. {canonicalExplanation(file)}
                </p>
              )}
            </div>

            <aside className={styles.facts}>
              <Facts file={file} />
            </aside>

            {locked && editJob ? <EditInFlight editJob={editJob} /> : null}
          </div>
        )}
      </section>
    </div>
  );
}

function headVersionNo(file: FileDocument): number | null {
  return file.versions.find((version) => version.id === file.currentVersionId)?.versionNo ?? null;
}

/**
 * How many pages the edit's walk must name, or null where the page cannot say.
 *
 * An edit is applied to the head's canonical, so its own page count is the
 * one that counts; a rail that is not windowed is a fallback for a canonical
 * that never reported one.
 */
function editablePageCount(file: FileDocument, railLimit: number): number | null {
  if (file.status !== 'VALIDATED' || !file.canonicalFile || !file.pages.length) {
    return null;
  }
  if (file.canonicalFile.pageCount) {
    return file.canonicalFile.pageCount;
  }
  return file.pages.length < railLimit ? file.pages.length : null;
}

/** Covers the document while Bindery works, so nothing stale can be acted on. */
function EditInFlight({ editJob }: { editJob: EditJob }) {
  const { job, versionNo } = editJob;
  return (
    <div className={styles.lock} role="status">
      <div className={styles.lockCard}>
        <Spinner size="lg" />
        <strong className={styles.lockTitle}>
          {`Editing — v${versionNo} is with Bindery (${job.status})`}
        </strong>
        <span className={styles.lockText}>
          The document behind is still the previous version: Keystone moves the head only once
          Bindery answers.
        </span>
      </div>
    </div>
  );
}

function EditStatus({ editJob }: { editJob: EditJob }) {
  const { job, versionNo } = editJob;
  if (isJobInFlight(job)) {
    return (
      <div className={styles.message} role="status">
        <Callout title={`Editing — v${versionNo} is with Bindery (${job.status})`}>
          The document below is still the previous version: Keystone moves the head only once
          Bindery answers.
        </Callout>
      </div>
    );
  }
  if (job.status === 'COMPLETE' && job.verdict === 'ok') {
    return (
      <div className={styles.message} role="status">
        <Callout title={`Edit applied — now on v${versionNo}`}>
          The document, rail and canonical below are the edited version.
        </Callout>
      </div>
    );
  }
  return (
    <div className={styles.message}>
      <Callout tone="danger" title={`Edit to v${versionNo} did not land`}>
        {job.status === 'FAILED'
          ? `The job died on the DLQ (${job.failureClass ?? 'unknown class'}).`
          : `Bindery rejected it (${job.verdict ?? 'no verdict'}).`}{' '}
        {job.error ?? ''} The file still serves the bytes it served before.
      </Callout>
    </div>
  );
}

/** Why there is no canonical, in the words the Keystone debugging notes use. */
function canonicalExplanation(file: FileDocument): string {
  const job = file.canonicalization;
  if (!job) {
    return 'No Bindery job was ever written — nothing was asked for.';
  }
  if (job.status === 'PENDING') {
    return 'The job has not been published yet.';
  }
  if (job.status === 'DISPATCHED') {
    return 'Bindery has the job and has not answered.';
  }
  if (job.status === 'FAILED') {
    return `The job died on the DLQ (${job.failureClass ?? 'unknown class'}).`;
  }
  return `Bindery answered and rejected it (${job.verdict ?? 'no verdict'}).`;
}

function Facts({ file }: { file: FileDocument }) {
  const job = file.canonicalization;
  const canonical = file.canonicalFile;
  return (
    <dl className={styles.rows}>
      {/* The canonical comes first because it is what every surface actually
          serves — the original is the document of record, not the thing a
          reader is looking at. Both are shown, because the pair is the whole
          point: it says what canonicalization bought. */}
      <Row term="Canonical">
        {canonical ? (
          <>
            {canonical.filename}
            <br />
            {canonical.contentType ?? '—'} · {formatBytes(canonical.sizeBytes)}{' '}
            <SavingPill canonical={canonical.sizeBytes} original={file.sizeBytes} /> ·{' '}
            {canonical.pageCount ?? '—'}p
            <br />
            <code>{canonical.checksum ?? '—'}</code>
          </>
        ) : (
          <em>none — {canonicalExplanation(file)}</em>
        )}
      </Row>
      <Row term="Original">
        {file.filename}
        <br />
        {file.contentType ?? '—'} · {formatBytes(file.sizeBytes)}
        {file.pageCount ? ` · ${file.pageCount}p` : ''}
        <br />
        <code>{file.checksum ?? '—'}</code>
      </Row>
      <Row term="Status">
        <Chip tone={file.status === 'VALIDATED' ? 'good' : 'warn'}>{file.status ?? '—'}</Chip>
      </Row>
      <Row term="Keystone id">
        <code>{file.id}</code>
      </Row>
      <Row term="app-api id">
        {file.extAppApiId ? <code>{file.extAppApiId}</code> : <em>none — Keystone only</em>}
      </Row>
      <Row term="Object">
        <code>
          {file.bucket ?? '—'}/{file.objectName ?? '—'}
        </code>
      </Row>
      <Row term="Owner">
        <code>{file.createdById ?? '—'}</code>
      </Row>
      <Row term="Created">{formatDate(file.createdAt)}</Row>
      <Row term="Validated">{formatDate(file.validatedAt)}</Row>
      <Row term="Migrated">
        {file.migratedAt ? (
          <>
            {formatDate(file.migratedAt)}
            {file.migratedFromUri ? (
              <>
                {' from '}
                <code>{file.migratedFromUri}</code>
              </>
            ) : (
              ' from inline bytes'
            )}
          </>
        ) : (
          <em>uploaded, not migrated</em>
        )}
      </Row>
      <Row term="Forked from">
        {file.duplicatedFromFileId ? <code>{file.duplicatedFromFileId}</code> : <em>—</em>}
      </Row>
      <Row term="Bindery job">
        {job ? (
          <>
            <Chip
              tone={job.status === 'COMPLETE' ? 'good' : job.status === 'FAILED' ? 'bad' : 'warn'}
            >
              {job.status ?? '—'}
            </Chip>{' '}
            {job.verdict ?? ''} {job.failureClass ?? ''}
            {job.error ? <span className={styles.error}>{job.error}</span> : null}
          </>
        ) : (
          <em>none</em>
        )}
      </Row>
      <Row term="Versions">
        <ul className={styles.versions}>
          {file.versions.map((version) => (
            <li key={version.id}>
              v{version.versionNo} · {formatBytes(version.sizeBytes)} ·{' '}
              {version.canonicalFileId ? 'canonical' : 'no canonical'}
              {version.id === file.currentVersionId ? ' · head' : ''}
            </li>
          ))}
          {file.versions.length ? null : <li>—</li>}
        </ul>
      </Row>
    </dl>
  );
}

/**
 * What canonicalization cost, as a share of the original.
 *
 * Under 100% is the win the pipeline exists for — a 2.4 MB phone photo served
 * as a few hundred KB — so it reads as good. Over 100% is not a bug and is
 * worth seeing: canonicalizing a small text-only PDF can add bytes, and a
 * caller wondering why a download got bigger should be able to find that here
 * rather than infer it.
 */
function SavingPill({
  canonical,
  original,
}: {
  canonical: number | null;
  original: number | null;
}) {
  if (!canonical || !original) {
    return null;
  }
  const percent = Math.round((canonical / original) * 100);
  return <Chip tone={percent <= 100 ? 'good' : 'warn'}>{percent}% of original</Chip>;
}

function Row({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div className={styles.row}>
      <dt>{term}</dt>
      <dd>{children}</dd>
    </div>
  );
}
